import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Pass 28bd — HypePets per-streamer leaderboard widget.
 *
 * Sibling of GemLeaderboardWidget — same visual language (animated
 * podium for the top 3, compact rows below, tweened counters,
 * rank-change arrows on every refresh), but rotates through several
 * leaderboard "views" on a timer so streamers get a single overlay
 * that celebrates the channel's HypePets community from every angle:
 *
 *   - Top Trainers       (totalLevels)
 *   - Master Collectors  (pets)
 *   - Shiny Hunters      (shinies)
 *   - Perfectionists     (perfects, quality=100 pets)
 *   - Crystal Investors  (crystalsInvested across owned pets)
 *
 * One backend fetch every 60s (top 200 viewers on this channel with
 * all metrics pre-computed); per-metric re-sort happens client-side
 * so rotations are instant and free.
 */

interface LeaderboardEntry {
  platformUsername: string;
  platform: 'twitch' | 'youtube' | 'kick';
  pets: number;
  totalLevels: number;
  shinies: number;
  perfects: number;
  crystalsInvested: number;
}

interface Fields {
  title?: string;
  limit?: number;
  showPlatformIcon?: boolean;
  variant?: 'podium' | 'compact';
  accentColor?: string;
  showCrown?: boolean;
  showRankChange?: boolean;
  /** Per-metric dwell time in ms. Default 8000 (8s) — long enough to
   *  read, short enough that all five rotate inside a minute. */
  rotateMs?: number;
  /** Subset of MetricId to cycle through. When omitted/empty,
   *  cycles all five. Lets a streamer who doesn't care about
   *  crystals trim the rotation. */
  metrics?: string[];
}

type MetricId = 'levels' | 'pets' | 'shinies' | 'perfects' | 'crystals';

interface MetricDef {
  id: MetricId;
  title: string;
  subtitle: string;
  icon: string;
  unit: string;
  accent: string;
  glow: string;
  /** Reads the metric's numeric value off a leaderboard entry. */
  read: (e: LeaderboardEntry) => number;
}

const METRICS: Record<MetricId, MetricDef> = {
  levels: {
    id: 'levels',
    title: 'Top Trainers',
    subtitle: 'Total Levels',
    icon: '🏆',
    unit: 'Lv',
    accent: '#a855f7',
    glow: 'rgba(168, 85, 247, 0.55)',
    read: (e) => e.totalLevels,
  },
  pets: {
    id: 'pets',
    title: 'Master Collectors',
    subtitle: 'Pets Owned',
    icon: '🐾',
    unit: 'pets',
    accent: '#22d3ee',
    glow: 'rgba(34, 211, 238, 0.55)',
    read: (e) => e.pets,
  },
  shinies: {
    id: 'shinies',
    title: 'Shiny Hunters',
    subtitle: 'Shiny Pets',
    icon: '✨',
    unit: '✨',
    accent: '#fbbf24',
    glow: 'rgba(251, 191, 36, 0.55)',
    read: (e) => e.shinies,
  },
  perfects: {
    id: 'perfects',
    title: 'Perfectionists',
    subtitle: 'Perfect Pets',
    icon: '💎',
    unit: '💎',
    accent: '#f472b6',
    glow: 'rgba(244, 114, 182, 0.55)',
    read: (e) => e.perfects,
  },
  crystals: {
    id: 'crystals',
    title: 'Crystal Investors',
    subtitle: 'Crystals Spent',
    icon: '🔮',
    unit: '◆',
    accent: '#60a5fa',
    glow: 'rgba(96, 165, 250, 0.55)',
    read: (e) => e.crystalsInvested,
  },
};

const ALL_METRIC_IDS: MetricId[] = ['levels', 'pets', 'shinies', 'perfects', 'crystals'];

const PLATFORM_COLORS: Record<LeaderboardEntry['platform'], string> = {
  twitch: '#9146ff',
  youtube: '#ff0000',
  kick: '#53fc18',
};

const PLATFORM_GLYPH: Record<LeaderboardEntry['platform'], string> = {
  twitch: 'T',
  youtube: 'Y',
  kick: 'K',
};

const TIER = {
  1: { gold: '#fbbf24', accent: '#fde68a', label: 'CHAMPION', icon: '👑' },
  2: { gold: '#cbd5e1', accent: '#e2e8f0', label: 'LEGEND', icon: '🥈' },
  3: { gold: '#d97706', accent: '#fbbf24', label: 'ELITE', icon: '🥉' },
} as const;

function formatN(n: number): string {
  return n.toLocaleString();
}

function useTweenedNumber(target: number, durationMs = 800): number {
  const [value, setValue] = useState(target);
  const startRef = useRef(target);
  const startTimeRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (target === value) return;
    startRef.current = value;
    startTimeRef.current = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - startTimeRef.current) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = Math.round(startRef.current + (target - startRef.current) * eased);
      setValue(v);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else rafRef.current = null;
    };
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, durationMs]);

  return value;
}

export function HypePetsLeaderboardWidget({
  streamerId,
  fields,
}: {
  streamerId: string;
  fields: Fields;
}) {
  const limit = Math.max(1, Math.min(50, Number(fields.limit) || 8));
  const customTitle = typeof fields.title === 'string' && fields.title.trim() ? fields.title : null;
  const showPlatformIcon = fields.showPlatformIcon !== false;
  const showCrown = fields.showCrown !== false;
  const showRankChange = fields.showRankChange !== false;
  const variant: 'podium' | 'compact' = fields.variant === 'compact' ? 'compact' : 'podium';
  const rotateMs = Math.max(2000, Math.min(60_000, Number(fields.rotateMs) || 8000));
  const accentOverride =
    typeof fields.accentColor === 'string' && /^#[0-9a-fA-F]{6}$/.test(fields.accentColor)
      ? fields.accentColor
      : null;

  // Whitelist incoming metric ids — drops unknowns (typos / stale
  // catalog) silently so a bad field can't crash the rotation.
  const enabledMetrics: MetricId[] = useMemo(() => {
    if (!Array.isArray(fields.metrics) || fields.metrics.length === 0) return ALL_METRIC_IDS;
    const picked = fields.metrics.filter((m): m is MetricId =>
      ALL_METRIC_IDS.includes(m as MetricId),
    );
    return picked.length > 0 ? picked : ALL_METRIC_IDS;
  }, [fields.metrics]);

  const [allRows, setAllRows] = useState<LeaderboardEntry[] | null>(null);
  const [metricIdx, setMetricIdx] = useState(0);
  // Prev-rank tracking keyed by `metric|platform|username` — separate
  // namespace per metric so flipping rotations doesn't paint every row
  // as "rank changed".
  const prevRanksRef = useRef<Map<string, number>>(new Map());
  const [rankChanges, setRankChanges] = useState<Map<string, number>>(new Map());

  // Backend fetch — 60s cadence, same as gem leaderboard.
  useEffect(() => {
    if (!streamerId) return;
    let cancelled = false;
    const load = () => {
      fetch(`/api/streamers/${streamerId}/hypepets/leaderboard?limit=200`)
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
        .then((json: { rows?: LeaderboardEntry[] }) => {
          if (cancelled) return;
          setAllRows(json.rows ?? []);
        })
        .catch((err) => {
          if (!cancelled) console.error('[hypepets-leaderboard] fetch failed', err);
        });
    };
    load();
    const id = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [streamerId]);

  // Metric rotation — disabled when only one metric is enabled.
  useEffect(() => {
    if (enabledMetrics.length <= 1) return;
    const id = setInterval(() => {
      setMetricIdx((i) => (i + 1) % enabledMetrics.length);
    }, rotateMs);
    return () => clearInterval(id);
  }, [enabledMetrics, rotateMs]);

  const activeMetricId = enabledMetrics[metricIdx % enabledMetrics.length] ?? enabledMetrics[0]!;
  const metric = METRICS[activeMetricId];
  const accentColor = accentOverride ?? metric.accent;

  // Sort + slice per-metric. Filter to rows with a non-zero value so
  // empty leaderboards (e.g. no shinies yet) don't pad with zero-shiny
  // accounts ranked by levels — confusing UX.
  const ranked = useMemo(() => {
    if (!allRows) return null;
    const filtered = allRows.filter((r) => metric.read(r) > 0);
    filtered.sort((a, b) => metric.read(b) - metric.read(a));
    return filtered.slice(0, limit);
  }, [allRows, limit, metric]);

  // Rank-change tracking per-metric. When the active metric flips, we
  // intentionally reset the prev-ranks so the new view starts clean;
  // arrows then only fire on actual rerank events within the same view.
  useEffect(() => {
    if (!ranked) return;
    const ns = `${activeMetricId}|`;
    const next = new Map<string, number>();
    const changes = new Map<string, number>();
    ranked.forEach((row, i) => {
      const key = `${ns}${row.platform}|${row.platformUsername}`;
      const prev = prevRanksRef.current.get(key);
      if (prev != null && prev !== i) changes.set(key, prev - i);
      next.set(key, i);
    });
    // Strip prev entries for OTHER metrics' namespaces so the map
    // doesn't grow unbounded across many rotations.
    for (const k of prevRanksRef.current.keys()) {
      if (!k.startsWith(ns)) prevRanksRef.current.delete(k);
    }
    for (const [k, v] of next) prevRanksRef.current.set(k, v);
    setRankChanges(changes);
  }, [ranked, activeMetricId]);

  const podium = useMemo(() => (ranked ?? []).slice(0, 3), [ranked]);
  const rest = useMemo(() => (ranked ?? []).slice(3), [ranked]);

  return (
    <>
      <style>{`
        @keyframes hv-hpl-pop-in {
          0%   { transform: translateY(12px) scale(0.96); opacity: 0; }
          60%  { transform: translateY(0)    scale(1.02); opacity: 1; }
          100% { transform: translateY(0)    scale(1);    opacity: 1; }
        }
        @keyframes hv-hpl-fade {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes hv-hpl-shimmer {
          0%   { transform: translateX(-100%); }
          60%  { transform: translateX(140%); }
          100% { transform: translateX(140%); }
        }
        @keyframes hv-hpl-icon-bob {
          0%, 100% { transform: scale(1) rotate(0deg); }
          50%      { transform: scale(1.08) rotate(8deg); }
        }
        @keyframes hv-hpl-crown-bob {
          0%, 100% { transform: translateY(0) rotate(-8deg); }
          50%      { transform: translateY(-4px) rotate(8deg); }
        }
        @keyframes hv-hpl-glow-pulse {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50%      { opacity: 0.85; transform: scale(1.08); }
        }
        @keyframes hv-hpl-progress {
          from { width: 0%; }
          to   { width: 100%; }
        }

        .hv-hpl-root {
          width: 100%;
          height: 100%;
          padding: 18px;
          box-sizing: border-box;
          background:
            radial-gradient(120% 80% at 20% 0%, var(--hv-bg-a) 0%, transparent 60%),
            radial-gradient(120% 80% at 80% 100%, var(--hv-bg-b) 0%, transparent 55%),
            linear-gradient(180deg, #0c0a1a 0%, #0a0617 100%);
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.05);
          box-shadow: 0 12px 40px var(--hv-bg-a), inset 0 1px 0 rgba(255,255,255,0.06);
          color: #fff;
          font-family: 'Poppins', system-ui, -apple-system, 'Segoe UI', sans-serif;
          display: flex;
          flex-direction: column;
          gap: 14px;
          overflow: hidden;
          position: relative;
          animation: hv-hpl-pop-in 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
          transition: box-shadow 0.6s ease;
        }
        .hv-hpl-root::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,0.4), transparent 50%),
            radial-gradient(1px 1px at 70% 70%, rgba(255,255,255,0.3), transparent 50%),
            radial-gradient(1.5px 1.5px at 40% 80%, rgba(255,255,255,0.25), transparent 50%),
            radial-gradient(1px 1px at 85% 15%, rgba(255,255,255,0.35), transparent 50%);
          pointer-events: none;
          opacity: 0.7;
        }

        .hv-hpl-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          position: relative;
          z-index: 1;
        }
        .hv-hpl-title {
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          color: #fff;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .hv-hpl-title-icon {
          font-size: 18px;
          display: inline-block;
          animation: hv-hpl-icon-bob 2.2s ease-in-out infinite;
          filter: drop-shadow(0 0 8px var(--hv-accent));
        }
        .hv-hpl-title-sub {
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.4);
        }
        .hv-hpl-progress {
          position: absolute;
          left: 0; right: 0; bottom: 0;
          height: 2px;
          background: rgba(255,255,255,0.06);
          overflow: hidden;
          z-index: 2;
        }
        .hv-hpl-progress::after {
          content: '';
          display: block;
          height: 100%;
          width: 0%;
          background: linear-gradient(90deg, var(--hv-accent) 0%, var(--hv-accent-soft) 100%);
          box-shadow: 0 0 8px var(--hv-accent);
          animation: hv-hpl-progress var(--hv-rotate-ms) linear infinite;
        }

        .hv-hpl-podium {
          display: flex;
          flex-direction: column;
          gap: 8px;
          position: relative;
          z-index: 1;
          animation: hv-hpl-fade 0.35s ease both;
        }
        .hv-hpl-podium-card {
          position: relative;
          padding: 12px 14px;
          border-radius: 12px;
          background:
            linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%);
          border: 1px solid rgba(255,255,255,0.08);
          display: grid;
          grid-template-columns: 36px 1fr auto;
          align-items: center;
          gap: 12px;
          overflow: hidden;
        }
        .hv-hpl-podium-card.tier-1 {
          background:
            linear-gradient(135deg, rgba(251,191,36,0.22) 0%, rgba(251,191,36,0.05) 100%);
          border-color: rgba(251,191,36,0.5);
          box-shadow: 0 0 28px rgba(251,191,36,0.32), inset 0 1px 0 rgba(255,255,255,0.15);
          padding: 16px 16px 16px 14px;
        }
        .hv-hpl-podium-card.tier-2 {
          background:
            linear-gradient(135deg, rgba(203,213,225,0.18) 0%, rgba(203,213,225,0.03) 100%);
          border-color: rgba(203,213,225,0.4);
          box-shadow: 0 0 18px rgba(203,213,225,0.22);
        }
        .hv-hpl-podium-card.tier-3 {
          background:
            linear-gradient(135deg, rgba(217,119,6,0.18) 0%, rgba(217,119,6,0.03) 100%);
          border-color: rgba(217,119,6,0.4);
          box-shadow: 0 0 18px rgba(217,119,6,0.22);
        }
        .hv-hpl-podium-card.tier-1 .hv-hpl-shimmer-pass {
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%);
          transform: translateX(-100%);
          animation: hv-hpl-shimmer 4.5s ease-in-out infinite;
          pointer-events: none;
        }
        .hv-hpl-glow-halo {
          position: absolute;
          inset: -20% -10%;
          background: radial-gradient(50% 60% at 50% 50%, var(--hv-tier-glow) 0%, transparent 70%);
          pointer-events: none;
          animation: hv-hpl-glow-pulse 3.2s ease-in-out infinite;
          z-index: 0;
        }

        .hv-hpl-rank-cluster {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
          position: relative;
          z-index: 1;
        }
        .hv-hpl-medal {
          font-size: 22px;
          line-height: 1;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));
        }
        .hv-hpl-tier-1 .hv-hpl-medal {
          font-size: 26px;
          animation: hv-hpl-crown-bob 2.4s ease-in-out infinite;
        }
        .hv-hpl-rank-num {
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.5px;
          color: var(--hv-tier-color);
          text-shadow: 0 0 8px var(--hv-tier-glow);
        }

        .hv-hpl-name-block {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
          position: relative;
          z-index: 1;
        }
        .hv-hpl-name {
          font-size: 16px;
          font-weight: 700;
          color: #fff;
          letter-spacing: 0.3px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .hv-hpl-tier-1 .hv-hpl-name { font-size: 18px; }
        .hv-hpl-tier-label {
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: var(--hv-tier-color);
          opacity: 0.85;
          text-shadow: 0 0 6px var(--hv-tier-glow);
        }

        .hv-hpl-platform-chip {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          border-radius: 5px;
          font-size: 10px;
          font-weight: 800;
          color: #fff;
          line-height: 1;
        }

        .hv-hpl-rank-arrow {
          font-size: 10px;
          font-weight: 800;
          padding: 1px 5px;
          border-radius: 4px;
          line-height: 1.2;
          display: inline-flex;
          align-items: center;
          gap: 2px;
        }
        .hv-hpl-rank-arrow.up   { background: rgba(34,197,94,0.18); color: #4ade80; }
        .hv-hpl-rank-arrow.down { background: rgba(239,68,68,0.18); color: #f87171; }

        .hv-hpl-amount-block {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 0;
          position: relative;
          z-index: 1;
        }
        .hv-hpl-amount {
          font-size: 20px;
          font-weight: 900;
          color: #fff;
          font-variant-numeric: tabular-nums;
          letter-spacing: -0.5px;
          text-shadow: 0 0 12px var(--hv-tier-glow);
          line-height: 1;
          display: flex;
          align-items: baseline;
          gap: 5px;
        }
        .hv-hpl-tier-1 .hv-hpl-amount { font-size: 26px; }
        .hv-hpl-amount-icon {
          font-size: 14px;
          display: inline-block;
          filter: drop-shadow(0 0 6px var(--hv-tier-glow));
        }
        .hv-hpl-amount-label {
          font-size: 8.5px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.5);
          margin-top: 2px;
        }

        .hv-hpl-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
          position: relative;
          z-index: 1;
          animation: hv-hpl-fade 0.35s ease both;
        }
        .hv-hpl-row {
          display: grid;
          grid-template-columns: 28px 1fr auto;
          align-items: center;
          gap: 10px;
          padding: 7px 12px;
          border-radius: 8px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.04);
          font-size: 13px;
        }
        .hv-hpl-row-rank {
          color: var(--hv-accent);
          font-weight: 800;
          font-variant-numeric: tabular-nums;
          font-size: 14px;
        }
        .hv-hpl-row-name {
          color: #e5e7eb;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .hv-hpl-row-amount {
          color: #fff;
          font-weight: 800;
          font-variant-numeric: tabular-nums;
          display: inline-flex;
          align-items: baseline;
          gap: 4px;
          font-size: 14px;
        }

        .hv-hpl-empty {
          font-size: 12px;
          color: rgba(255,255,255,0.5);
          text-align: center;
          padding: 16px 8px;
          letter-spacing: 0.5px;
        }
      `}</style>

      <div
        className="hv-hpl-root"
        style={
          {
            ['--hv-accent']: accentColor,
            ['--hv-accent-soft']: accentColor + '88',
            ['--hv-bg-a']: hexWithAlpha(accentColor, 0.22),
            ['--hv-bg-b']: hexWithAlpha(accentColor, 0.1),
            ['--hv-rotate-ms']: `${rotateMs}ms`,
          } as React.CSSProperties
        }
        // Re-key per metric so the CSS rotation animation restarts and
        // the fade-in re-plays. Without this, the fill bar keeps its
        // prior phase across rotations and feels jittery.
        key={activeMetricId}
      >
        <div className="hv-hpl-header">
          <div className="hv-hpl-title">
            <span className="hv-hpl-title-icon">{metric.icon}</span>
            <span>{customTitle ?? metric.title}</span>
          </div>
          <div className="hv-hpl-title-sub">{metric.subtitle}</div>
        </div>

        {ranked === null ? (
          <div className="hv-hpl-empty">Loading…</div>
        ) : ranked.length === 0 ? (
          <div className="hv-hpl-empty">
            No {metric.title.toLowerCase()} yet — viewers earn pets on this channel by chatting!
          </div>
        ) : variant === 'compact' ? (
          <div className="hv-hpl-list">
            {ranked.map((r, i) => (
              <CompactRow
                key={`${r.platform}-${r.platformUsername}`}
                row={r}
                rank={i + 1}
                value={metric.read(r)}
                unit={metric.unit}
                accentColor={accentColor}
                showPlatformIcon={showPlatformIcon}
                rankDelta={
                  showRankChange
                    ? (rankChanges.get(`${activeMetricId}|${r.platform}|${r.platformUsername}`) ??
                      0)
                    : 0
                }
              />
            ))}
          </div>
        ) : (
          <>
            <div className="hv-hpl-podium">
              {podium.map((r, i) => (
                <PodiumCard
                  key={`${r.platform}-${r.platformUsername}`}
                  row={r}
                  rank={(i + 1) as 1 | 2 | 3}
                  value={metric.read(r)}
                  unit={metric.unit}
                  glow={metric.glow}
                  showCrown={showCrown}
                  showPlatformIcon={showPlatformIcon}
                  rankDelta={
                    showRankChange
                      ? (rankChanges.get(`${activeMetricId}|${r.platform}|${r.platformUsername}`) ??
                        0)
                      : 0
                  }
                />
              ))}
            </div>
            {rest.length > 0 && (
              <div className="hv-hpl-list">
                {rest.map((r, i) => (
                  <CompactRow
                    key={`${r.platform}-${r.platformUsername}`}
                    row={r}
                    rank={i + 4}
                    value={metric.read(r)}
                    unit={metric.unit}
                    accentColor={accentColor}
                    showPlatformIcon={showPlatformIcon}
                    rankDelta={
                      showRankChange
                        ? (rankChanges.get(
                            `${activeMetricId}|${r.platform}|${r.platformUsername}`,
                          ) ?? 0)
                        : 0
                    }
                  />
                ))}
              </div>
            )}
          </>
        )}

        {enabledMetrics.length > 1 && <div className="hv-hpl-progress" />}
      </div>
    </>
  );
}

function PodiumCard({
  row,
  rank,
  value,
  unit,
  glow,
  showCrown,
  showPlatformIcon,
  rankDelta,
}: {
  row: LeaderboardEntry;
  rank: 1 | 2 | 3;
  value: number;
  unit: string;
  glow: string;
  showCrown: boolean;
  showPlatformIcon: boolean;
  rankDelta: number;
}) {
  const tier = TIER[rank];
  const tweened = useTweenedNumber(value);
  return (
    <div
      className={`hv-hpl-podium-card tier-${rank} hv-hpl-tier-${rank}`}
      style={
        {
          ['--hv-tier-color']: tier.gold,
          ['--hv-tier-glow']: rank === 1 ? glow : tier.gold + '88',
        } as React.CSSProperties
      }
    >
      {rank === 1 && <div className="hv-hpl-glow-halo" />}
      {rank === 1 && <div className="hv-hpl-shimmer-pass" />}
      <div className="hv-hpl-rank-cluster">
        <span className="hv-hpl-medal">{rank === 1 && showCrown ? tier.icon : tier.icon}</span>
        <span className="hv-hpl-rank-num">#{rank}</span>
      </div>
      <div className="hv-hpl-name-block">
        <div className="hv-hpl-name">
          {showPlatformIcon && (
            <span
              className="hv-hpl-platform-chip"
              style={{ background: PLATFORM_COLORS[row.platform] }}
              title={row.platform}
            >
              {PLATFORM_GLYPH[row.platform]}
            </span>
          )}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {row.platformUsername}
          </span>
          <RankDeltaBadge delta={rankDelta} />
        </div>
        <div className="hv-hpl-tier-label">{tier.label}</div>
      </div>
      <div className="hv-hpl-amount-block">
        <div className="hv-hpl-amount">
          <span>{formatN(tweened)}</span>
          <span className="hv-hpl-amount-icon">{unit}</span>
        </div>
        <div className="hv-hpl-amount-label">
          {unit === 'Lv' ? 'Levels' : unit === 'pets' ? 'Pets' : ''}
        </div>
      </div>
    </div>
  );
}

function CompactRow({
  row,
  rank,
  value,
  unit,
  showPlatformIcon,
  accentColor,
  rankDelta,
}: {
  row: LeaderboardEntry;
  rank: number;
  value: number;
  unit: string;
  showPlatformIcon: boolean;
  accentColor: string;
  rankDelta: number;
}) {
  const tweened = useTweenedNumber(value);
  return (
    <div className="hv-hpl-row">
      <span className="hv-hpl-row-rank" style={{ color: accentColor }}>
        #{rank}
      </span>
      <span className="hv-hpl-row-name">
        {showPlatformIcon && (
          <span
            className="hv-hpl-platform-chip"
            style={{ background: PLATFORM_COLORS[row.platform] }}
            title={row.platform}
          >
            {PLATFORM_GLYPH[row.platform]}
          </span>
        )}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.platformUsername}</span>
        <RankDeltaBadge delta={rankDelta} />
      </span>
      <span className="hv-hpl-row-amount">
        {formatN(tweened)} <span aria-hidden>{unit}</span>
      </span>
    </div>
  );
}

function RankDeltaBadge({ delta }: { delta: number }) {
  if (delta === 0) return null;
  if (delta > 0) {
    return (
      <span className="hv-hpl-rank-arrow up" title={`Up ${delta}`}>
        ▲ {delta}
      </span>
    );
  }
  return (
    <span className="hv-hpl-rank-arrow down" title={`Down ${-delta}`}>
      ▼ {-delta}
    </span>
  );
}

/** Best-effort alpha overlay for the accent-driven background tint.
 *  Accepts #rrggbb only (the field validator rejects anything else
 *  upstream) so this is straight hex → rgba. */
function hexWithAlpha(hex: string, alpha: number): string {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return `rgba(168, 85, 247, ${alpha})`;
  const n = parseInt(m[1]!, 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
